/// <amd-module name="Analytics/Shared/Common/Sources/AnalyticsODataClientBase" />
import { AnalyticsODataVersion, BatchRequestMode, ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { IAnalyticsODataClient } from "Analytics/Common/RestClient/AnalyticsODataClient";
import { ICacheableQueryService } from "Reporting/Common/CacheableQueryService";
import { ICacheableQuery } from "Reporting/Common/ICacheableQuery";
import { IVssPageContext } from "VSS/Platform/Context";
import { ITfsPageService } from "Tfs/Platform/Page";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";

export interface IODataQueryResponse {
    value: any;
    "@odata.nextLink": string;
    isCachedData: boolean;
}

class AnalyticsODataQueryByOptions implements ICacheableQuery<IODataQueryResponse> {
    constructor(private _command: string, private _oDataQueryOptions: ODataQueryOptions, private pageContext: IVssPageContext) {}

    public runQuery(): Promise<IODataQueryResponse> {
        const client = this.pageContext.getRestClient<IAnalyticsODataClient>("IAnalyticsODataClient");
        return client.query(this._command, this._oDataQueryOptions);
    }

    public getKey(): string {
        const clonedOptions = { ...this._oDataQueryOptions };
        clonedOptions.pageContext = undefined;
        return JSON.stringify(clonedOptions);
    }
}

class AnalyticsODataQueryByUrl implements ICacheableQuery<IODataQueryResponse> {
    constructor(
        private _command: string,
        private _odataQueryUrl: string,
        private _oDataQueryOptions: ODataQueryOptions,
        private pageContext: IVssPageContext
    ) {}

    public runQuery(): Promise<IODataQueryResponse> {
        const client = this.pageContext.getRestClient<IAnalyticsODataClient>("IAnalyticsODataClient");
        return client.runNextLink(this._command, this._odataQueryUrl, this._oDataQueryOptions);
    }

    public getKey(): string {
        return this._odataQueryUrl;
    }
}

export abstract class AnalyticsODataClientBase {
    private _cacheableQueryService: ICacheableQueryService;

    constructor(private _command: string, public pageContext: IVssPageContext) {
        this._cacheableQueryService = this.pageContext.getService<ICacheableQueryService>("ICacheableQueryService");
    }

    protected queryOData(queryOptions: ODataQueryOptions, fetchSinglePage?: boolean): Promise<IODataQueryResponse> {
        // Setting some queryOptions
        queryOptions.oDataVersion = latestODataVersion;
        queryOptions.followNextLink = !fetchSinglePage;
        queryOptions.batchRequestMode = BatchRequestMode.AutoSelect;
        queryOptions.pageContext = this.pageContext;

        const _oDataQuery = new AnalyticsODataQueryByOptions(this._command, queryOptions, this.pageContext);
        return this._getQueryResult(_oDataQuery);
    }

    protected queryODataByUrl(requestUrl: string): Promise<IODataQueryResponse> {
        const queryOptions: ODataQueryOptions = {
            oDataVersion: latestODataVersion,
            batchRequestMode: BatchRequestMode.AutoSelect,
            pageContext: this.pageContext
        } as ODataQueryOptions;
        const _oDataQuery = new AnalyticsODataQueryByUrl(this._command, requestUrl, queryOptions, this.pageContext);
        return this._getQueryResult(_oDataQuery);
    }
    protected _getProjectId(): string {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            return tfsPageData.project.id;
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private async _getQueryResult(oDataQuery: AnalyticsODataQueryByOptions | AnalyticsODataQueryByUrl): Promise<IODataQueryResponse> {
        const isCachedData: boolean = this._cacheableQueryService.hasCachedEntry(oDataQuery);
        const responseData = await this._cacheableQueryService.getCacheableQueryResult(oDataQuery);
        responseData.isCachedData = isCachedData;
        return responseData;
    }
}
